import { Button } from "./ui/button";
import useAuth from "../hooks/useAuth";
import { Loader2, Pencil, Plus } from "lucide-react";
import EditProfileModal from "./EditProfileModal";
import { useCallback, useEffect, useMemo, useState } from "react";
import AddSettlementAccountModal from "./AddSettlementAccountModal";
import useSWR from "swr";
import { fetcher, handleMutation } from "../util/axios";
import useSWRMutation from "swr/mutation";
import { message, Radio } from "antd";
import { useCurrency } from "../hooks/useCurrency";
import requestErrorHandler from "../util/requestErrorHandler";
import dayjs from "dayjs";

type Account = {
  id: string;
  accountName: string;
  accountNumber: string;
  bankName: string;
  bankCode: string;
  isActive: boolean;
}

function BusinessInformation() {
  const [selectedAccount, setSelectedAccount] = useState<Account | undefined>();
  const [ formatCurrency ] = useCurrency()
  // const [amount, setAmount] = useState('')
  const { user } = useAuth();
  const [openModal, setOpenModal] = useState(false);
  const [openSettlementModal, setOpenSettlementModal] = useState(false);

  const { data, isLoading: isLoadingAccounts, mutate } = useSWR(
    { url: "/fleet/settlement-accounts" },
    fetcher,
  );

  const { data: payable } = useSWR(
    { url: "/fleet/amount-payable" },
    fetcher,
  );
  const { trigger: activateAccount, isMutating: isActivating } = useSWRMutation(
    `fleet/settlement-accounts/${selectedAccount?.id}/activate`,
    handleMutation,
  );

  const accounts: Account[] = useMemo(() => {
    return data?.data || [];
  }, [data])

  const handleActivateAccount = useCallback(async () => {
    if (!selectedAccount) return;

    try {
      const res = await activateAccount();
      
      if (res) {
        message.success("Account activated successfully");
      }
    } catch (error) {
      requestErrorHandler(error);
      console.error(error)
    }
  }, [activateAccount, selectedAccount]);

  useEffect(() => {
    if (selectedAccount) {
      handleActivateAccount();
    }
  }, [selectedAccount, handleActivateAccount])
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <section>
        <h2 className='text-lg font-semibold !mb-8'>
          {user?.fleetOwner?.companyName}
        </h2>
        <div className="mb-10">
          <div className='flex items-end gap-3 mb-4'>
            <div className="h-[184px] w-[143px] border border-gray-200 rounded-sm">
              <img
                src={user?.fleetOwner?.cacCertificate}
                alt="CAC certificate"
                className="h-full w-full border rounded p-1"
              />
            </div>
            <Button variant="ghost" className='pb-0' onClick={() => setOpenModal(true)}>
              <Pencil />
            </Button>
          </div>
          <p className="flex items-center gap-1 !mb-1">
            <span className="text-gray-400 font-semibold">Amount Payable:</span>
            <span className="text-primary text-lg font-bold">{formatCurrency(payable?.data?.amountPayable || 0)}</span>
          </p>                      
          <p className="flex items-center gap-1 !mb-1">
            <span className="text-gray-400 font-semibold">Payout Frequency:</span>
            <span className="text-primary text-lg font-bold capitalize">{user?.fleetOwner?.payoutFrequency}</span>
          </p>
          <p className="flex items-center gap-1 !mb-1">
            <span className="text-gray-400 font-semibold">Next Payout Date:</span>
            <span className="text-primary text-lg font-bold">{user?.fleetOwner?.nextPayoutDate ? dayjs(user?.fleetOwner?.nextPayoutDate).format("YYYY-MM-DD") : "Not Set"}</span>
          </p>
          <p className='text-gray-400 font-semibold !mb-1'>
            {user?.fleetOwner?.phone}
          </p>
        </div>
      </section>
      <section>
        <div className="md:flex justify-end mb-4">
          <Button className="" onClick={() => setOpenSettlementModal(true)}>
            <Plus />
            <span>
              Add Settlement Account
            </span>
          </Button>
        </div>
        {(!isLoadingAccounts && accounts.length > 0) && (<>
          <div className="space-y-2 h-52 overflow-auto my-4">
            {isLoadingAccounts && (<div className="mt-20 text-center"><Loader2 className="animate-spin size-8" /></div>)}
            {(!isLoadingAccounts && accounts.length < 1) && (<div className="mt-20 text-center">
              <p className="font-medium text-sm">No saved settlement account</p>
            </div>)}

            {accounts.map((account) => (
              <div key={account.id} className="flex items-center gap-4">
                {selectedAccount?.id === account.id && isActivating 
                  ? (<Loader2 className="animate-spin" />)
                  : (<Radio
                      defaultChecked={account.isActive}
                      value={account}
                      name={account.id}
                      onChange={() => setSelectedAccount({...account})}
                    />
                  )}
                <div className="flex-1 flex flex-col gap-1">
                  <span className="font-medium">{account.accountName}</span>
                  <p className="flex items-center justify-between text-xs">
                    <span>{account.accountNumber}</span>
                    <span>{account.bankName}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
          {/* <div className="flex items-center gap-4">
            <Input
              id="amount"
              name="amount"
              type="number"
              placeholder="Enter Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="flex-1"
              size="large"
            />
            <Button disabled={isRequesting} onClick={handleRequestPayout}>
              {isRequesting
              ? (<Loader2 className="animate-spin" />)
              : "Request Payout"}
            </Button>
          </div> */}
        </>
        )}
      </section>
      <EditProfileModal
        open={openModal}
        user={user}
        onCancel={() => setOpenModal(false)}
      />
      <AddSettlementAccountModal
        open={openSettlementModal}
        user={user}
        onCancel={() => {
          setOpenSettlementModal(false)
          mutate();
        }}
      />   
    </div>
  )
}

export default BusinessInformation;